import { FeatureTypes } from "entity-app/constants";

export type MaterialListItem = {
  id: string;
  name: string;
};

export type CategorizedMaterials = {
  linkedToSubmittal: MaterialListItem[];
  hasSubmittalPlan: MaterialListItem[];
  noSubmittalPlan: MaterialListItem[];
};

export type PlanOffsetDetail = {
  materialId: string;
  prepDuration: number | null;
  reviewDuration: number | null;
};

export type DurationPlaceholder =
  | { kind: "value"; value: number }
  | { kind: "blank" }
  | { kind: "default"; value: number };

/** Submittal Plan offset sequences: 1 = Prep Time, 2 = Review Time. */
export const SUBMITTAL_PLAN_OFFSET_SEQUENCE = {
  PREP_TIME: 1,
  REVIEW_TIME: 2
} as const;

function matchesFeatureType(featureType: unknown, type: FeatureTypes): boolean {
  return featureType === type || featureType === FeatureTypes[type];
}

function materialDisplayName(row: {
  sequence_id?: number | string;
  number?: string;
  title?: string;
  id: string;
}): MaterialListItem {
  const sequenceId = row.sequence_id ?? row.number ?? "";
  const title = row.title || "";
  return {
    id: row.id,
    name: sequenceId ? `${sequenceId} - ${title}` : title || row.id
  };
}

function hasLinkedSubmittal(row: { linked_items?: any[] }) {
  return (row.linked_items || []).some((item: any) =>
    matchesFeatureType(item?.feature_type, FeatureTypes.SUBMITTALS)
  );
}

export function getSubmittalPlanLinkedItem(row: { linked_items?: any[] }) {
  return (
    (row.linked_items || []).find((item: any) =>
      matchesFeatureType(
        item?.feature_type,
        FeatureTypes.PLANNING_ONLY_SUBMITTAL
      )
    ) || null
  );
}

export function categorizeMaterialsForSubmittalPlan(
  selectedRows: Array<{
    id: string;
    title?: string;
    sequence_id?: number | string;
    number?: string;
    linked_items?: any[];
  }>
): CategorizedMaterials {
  const linkedToSubmittal: MaterialListItem[] = [];
  const hasSubmittalPlan: MaterialListItem[] = [];
  const noSubmittalPlan: MaterialListItem[] = [];

  selectedRows.forEach((row) => {
    const item = materialDisplayName(row);
    if (hasLinkedSubmittal(row)) {
      linkedToSubmittal.push(item);
    } else if (getSubmittalPlanLinkedItem(row)) {
      hasSubmittalPlan.push(item);
    } else {
      noSubmittalPlan.push(item);
    }
  });

  return { linkedToSubmittal, hasSubmittalPlan, noSubmittalPlan };
}

function toDuration(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function parsePlanOffsetDetails(
  featureInstances: any[] | undefined,
  materialIdByPlanId: Record<string, string>
): PlanOffsetDetail[] {
  if (!featureInstances?.length) return [];

  return featureInstances.map((inst) => {
    const milestones = [...(inst.workflow_instance_milestones || [])].sort(
      (a: any, b: any) => (a.sequence_no || 0) - (b.sequence_no || 0)
    );
    // Offset sequence 1 = Prep Time, 2 = Review Time. Offsets sit on the
    // milestone they lead into, so collect them in workflow order instead of
    // matching milestone.sequence_no.
    const offsets = milestones.flatMap(
      (m: any) => m.feature_workflow_instance_offsets_as_src || []
    );
    const prepOffset =
      offsets[SUBMITTAL_PLAN_OFFSET_SEQUENCE.PREP_TIME - 1] || null;
    const reviewOffset =
      offsets[SUBMITTAL_PLAN_OFFSET_SEQUENCE.REVIEW_TIME - 1] || null;

    return {
      materialId: materialIdByPlanId[inst.id] || "",
      prepDuration: toDuration(prepOffset?.duration),
      reviewDuration: toDuration(reviewOffset?.duration)
    };
  });
}

/**
 * Placeholder rules (evaluated separately per duration field):
 * - All selected materials have the same plan days -> that value
 * - Mixed selection (plans + no plans), or plan days differ -> blank
 * - None of the selected materials have a Submittal Plan -> system default
 */
export function resolveDurationPlaceholder(
  durations: Array<number | null | undefined>,
  options: {
    hasAnyPlan: boolean;
    isMixedSelection: boolean;
    systemDefault: number;
  }
): DurationPlaceholder {
  const { hasAnyPlan, isMixedSelection, systemDefault } = options;

  if (isMixedSelection) {
    return { kind: "blank" };
  }

  if (!hasAnyPlan) {
    return { kind: "default", value: systemDefault };
  }

  const defined = durations
    .map((d) => toDuration(d))
    .filter((d): d is number => d !== null);

  if (defined.length === 0) {
    return { kind: "blank" };
  }

  const first = defined[0];
  if (
    defined.every((d) => d === first) &&
    defined.length === durations.length
  ) {
    return { kind: "value", value: first };
  }

  return { kind: "blank" };
}

export function durationPlaceholderText(ph: DurationPlaceholder): string {
  if (ph.kind === "blank") return "";
  return String(ph.value);
}

export function durationFieldValue(ph: DurationPlaceholder): number | null {
  if (ph.kind === "blank") return null;
  return ph.value;
}

export function parsePositiveIntegerInput(
  value: string | number | null | undefined
): number | null {
  if (value === null || value === undefined || value === "") return null;
  const num = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(num) || num < 0 || !Number.isInteger(num)) return null;
  return num;
}

export function getEffectiveDuration(
  fieldValue: number | null,
  placeholder: DurationPlaceholder,
  mode: "create" | "update"
): number | null {
  if (fieldValue !== null) return fieldValue;
  if (mode === "update") {
    // Empty field leaves existing plan duration unchanged.
    return null;
  }
  // Create: fall back to placeholder when concrete (shared value or default).
  if (placeholder.kind === "value" || placeholder.kind === "default") {
    return placeholder.value;
  }
  return null;
}

export function canSaveSubmittalPlanBulk(params: {
  createCount: number;
  updateCount: number;
  prepField: number | null;
  reviewField: number | null;
  prepPlaceholder: DurationPlaceholder;
  reviewPlaceholder: DurationPlaceholder;
}): boolean {
  const {
    createCount,
    updateCount,
    prepField,
    reviewField,
    prepPlaceholder,
    reviewPlaceholder
  } = params;

  if (createCount === 0 && updateCount === 0) return false;

  if (createCount > 0) {
    const prep = getEffectiveDuration(prepField, prepPlaceholder, "create");
    const review = getEffectiveDuration(
      reviewField,
      reviewPlaceholder,
      "create"
    );
    if (prep === null || review === null) return false;
  }

  if (updateCount > 0 && createCount === 0) {
    // Empty fields leave duration unchanged — need at least one filled field.
    if (prepField === null && reviewField === null) return false;
  }

  return true;
}

export type PlanOffsetInput = { sequence: number; duration: number };

export function buildPlanOffsetInputs(
  prep: number | null,
  review: number | null
): PlanOffsetInput[] {
  const offsets: PlanOffsetInput[] = [];
  if (prep !== null) {
    offsets.push({
      sequence: SUBMITTAL_PLAN_OFFSET_SEQUENCE.PREP_TIME,
      duration: prep
    });
  }
  if (review !== null) {
    offsets.push({
      sequence: SUBMITTAL_PLAN_OFFSET_SEQUENCE.REVIEW_TIME,
      duration: review
    });
  }
  return offsets;
}

function pluralizedPlans(count: number): string {
  return `${count} Submittal Plan${count === 1 ? "" : "s"}`;
}

export function buildSubmittalPlanResultMessage(
  created: number,
  updated: number
): string {
  const parts: string[] = [];
  if (created > 0) parts.push(`${pluralizedPlans(created)} created`);
  if (updated > 0) parts.push(`${pluralizedPlans(updated)} updated`);
  return parts.join(". ") || "Updated successfully.";
}

export function buildSubmittalPlanBulkEditVariables(
  materialIds: string[],
  offsets: PlanOffsetInput[]
) {
  return {
    feature_instance_type_id: FeatureTypes.MATERIALS,
    feature_instance_ids: materialIds,
    updates: {
      planning_only_submittal: {
        title: "Submittal Plan",
        number: null,
        offsets
      }
    }
  };
}

export function getSystemDefaultPlanDurations(
  workflowDefaults:
    | Array<{
        feature_id: number;
        workflow_data?: Array<{
          offset_name: string;
          offset_value?: number;
        }>;
      }>
    | undefined
): { prep: number; review: number } {
  const submittalDefault = workflowDefaults?.find(
    (x) => (x.feature_id as FeatureTypes) === FeatureTypes.SUBMITTALS
  );

  const prep =
    submittalDefault?.workflow_data?.find(
      (x) => x.offset_name === "SC Submittal Prep"
    )?.offset_value || 0;

  const reviewOffsetNames = new Set([
    "Design Review",
    "GC Review",
    "Final GC Review"
  ]);
  const review =
    submittalDefault?.workflow_data?.reduce((sum, item) => {
      return reviewOffsetNames.has(item.offset_name)
        ? sum + (item.offset_value || 0)
        : sum;
    }, 0) || 0;

  return { prep, review };
}
